import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { GameMode, Difficulty } from '@/types';
import { DIFFICULTY_CONFIG } from '@/data/provinces';
import clsx from 'clsx';

interface GameControlsProps {
  onHintUsed?: (provinceId: string) => void;
}

export const GameControls: React.FC<GameControlsProps> = ({ onHintUsed }) => {
  const {
    mode,
    difficulty,
    score,
    timeRemaining,
    hintsUsed,
    maxHints,
    isGameStarted,
    isGameCompleted,
    isPaused,
    showProvinceNames, // 从gameStore获取
    showAbbreviations, // 从gameStore获取
    renderKey, // 添加renderKey用于强制重新渲染
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    useHint,
    toggleProvinceNames, // 从gameStore获取
    toggleAbbreviations, // 从gameStore获取
    getCompletionRate
  } = useGameStore();
  
  const [selectedMode, setSelectedMode] = useState<GameMode>('free');
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty>('easy');
  
  // 调试日志 - 仅在开发环境显示
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('GameControls 渲染:', {
      isGameStarted,
      isGameCompleted,
      renderKey,
      timestamp: new Date().toLocaleTimeString()
    });
  }
  
  // 格式化时间显示
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // 处理提示功能
  const handleUseHint = () => {
    const hintedProvinceId = useHint();
    if (hintedProvinceId) {
      onHintUsed?.(hintedProvinceId);
    }
  };
  
  // 游戏模式选项
  const gameModes: { value: GameMode; label: string; description: string; icon: string }[] = [
    {
      value: 'free',
      label: '自由模式',
      description: '无时间限制，轻松拼图',
      icon: '🎯'
    },
    {
      value: 'timed',
      label: '限时挑战',
      description: '在规定时间内完成拼图',
      icon: '⏱️'
    },
    {
      value: 'quiz',
      label: '知识问答',
      description: '拼图+地理知识问答',
      icon: '🧠'
    }
  ];
  
  // 难度选项 - 动态生成描述
  const difficulties: { value: Difficulty; label: string; description: string; color: string }[] = [
    {
      value: 'easy',
      label: '简单',
      description: `随机${DIFFICULTY_CONFIG.easy.count}个省份，适合初学者`,
      color: 'green'
    },
    {
      value: 'medium',
      label: '中等',
      description: `随机${DIFFICULTY_CONFIG.medium.count}个省份，有一定挑战`,
      color: 'yellow'
    },
    {
      value: 'hard',
      label: '困难',
      description: '全部省份，专家级挑战',
      color: 'red'
    }
  ];

  // 获取当前难度信息
  const getCurrentDifficultyInfo = () => {
    return difficulties.find(d => d.value === difficulty);
  };

  // 获取下一个难度信息
  const getNextDifficultyInfo = () => {
    const difficultyOrder: Difficulty[] = ['easy', 'medium', 'hard'];
    const currentIndex = difficultyOrder.indexOf(difficulty);
    const nextDifficulty = difficultyOrder[(currentIndex + 1) % difficultyOrder.length];
    return difficulties.find(d => d.value === nextDifficulty);
  };
  
  // 如果游戏未开始，显示游戏设置界面
  if (!isGameStarted) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-2 space-y-2 
        min-h-[900px] lg:min-h-[1100px] xl:min-h-[1300px] 2xl:min-h-[1500px] 
        flex flex-col w-full">
        <div className="text-center flex-shrink-0">
          <p className="text-sm text-gray-600">
            选择模式和难度
          </p>
        </div>
        
        {/* 游戏模式选择 */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-3">游戏模式</h3>
          <div className="space-y-2">
            {gameModes.map(gameMode => (
              <motion.button
                key={gameMode.value}
                className={clsx(
                  'w-full p-3 rounded-lg border-2 text-left transition-all duration-200',
                  {
                    'border-blue-500 bg-blue-50': selectedMode === gameMode.value,
                    'border-gray-200 bg-gray-50 hover:border-gray-300': selectedMode !== gameMode.value
                  }
                )}
                onClick={() => setSelectedMode(gameMode.value)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start space-x-2">
                  <span className="text-lg mt-0.5">{gameMode.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 text-sm">{gameMode.label}</div>
                    <div className="text-xs text-gray-600 leading-tight">{gameMode.description}</div>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
        
        {/* 难度选择 */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-3">难度等级</h3>
          <div className="space-y-2">
            {difficulties.map(diff => (
              <motion.button
                key={diff.value}
                className={clsx(
                  'w-full p-3 rounded-lg border-2 text-left transition-all duration-200',
                  {
                    'border-green-500 bg-green-50': selectedDifficulty === diff.value && diff.color === 'green',
                    'border-yellow-500 bg-yellow-50': selectedDifficulty === diff.value && diff.color === 'yellow',
                    'border-red-500 bg-red-50': selectedDifficulty === diff.value && diff.color === 'red',
                    'border-gray-200 bg-gray-50 hover:border-gray-300': selectedDifficulty !== diff.value
                  }
                )}
                onClick={() => setSelectedDifficulty(diff.value)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="font-medium text-gray-800 text-sm">{diff.label}</div>
                <div className="text-xs text-gray-600 leading-tight">{diff.description}</div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* 开始游戏按钮 */}
        <motion.button
          className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
          onClick={() => startGame(selectedMode, selectedDifficulty)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          🚀 开始游戏
        </motion.button>
        {/* 随机提示 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <span className="text-blue-500 mt-0.5 text-sm">🎲</span>
            <div className="text-xs text-blue-800 leading-tight">
              <strong>随机挑战：</strong>每次游戏都会随机选择不同的省份，增加游戏的趣味性和挑战性！
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 游戏进行中的控制面板
  return (
    <div className="bg-white rounded-lg shadow-sm p-2 space-y-2 
      min-h-[900px] lg:min-h-[1100px] xl:min-h-[1300px] 2xl:min-h-[1500px] 
      flex flex-col w-full">
      {/* 游戏信息头部 */}
      <div className="space-y-2 flex-shrink-0">
        <div className="grid grid-cols-1 gap-1 text-center">
          <div>
            <div className="text-sm font-bold text-blue-600">{score}</div>
            <div className="text-xs text-gray-500">分数</div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-purple-600">
              {getCompletionRate().toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500">完成度</div>
          </div>
        </div>
        
        {mode !== 'free' && (
          <div className="text-center">
            <div className={clsx(
              'text-xl font-bold',
              {
                'text-green-600': timeRemaining > 60,
                'text-yellow-600': timeRemaining <= 60 && timeRemaining > 30,
                'text-red-600': timeRemaining <= 30
              }
            )}>
              {formatTime(timeRemaining)}
            </div>
            <div className="text-xs text-gray-500">剩余时间</div>
          </div>
        )}
        
        {/* 游戏控制按钮 */}
        <div className="flex items-center space-x-1">
          {!isGameCompleted && (
            <>
              <motion.button
                className={clsx(
                  'flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200',
                  {
                    'bg-yellow-500 text-white hover:bg-yellow-600': !isPaused,
                    'bg-green-500 text-white hover:bg-green-600': isPaused
                  }
                )}
                onClick={isPaused ? resumeGame : pauseGame}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPaused ? '▶️' : '⏸️'}
              </motion.button>
            </>
          )}
          
          <motion.button
            className="flex-1 px-2 py-1.5 bg-gray-500 text-white rounded text-xs font-medium hover:bg-gray-600 transition-colors duration-200"
            onClick={resetGame}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🔄
          </motion.button>
        </div>
      </div>
      
      {/* 功能按钮区域 */}
      <div className="space-y-2">
        {/* 提示按钮 */}
        <motion.button
          className={clsx(
            'w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
            {
              'bg-blue-500 text-white hover:bg-blue-600': hintsUsed < maxHints,
              'bg-gray-300 text-gray-500 cursor-not-allowed': hintsUsed >= maxHints
            }
          )}
          onClick={handleUseHint}
          disabled={hintsUsed >= maxHints || isGameCompleted}
          whileHover={hintsUsed < maxHints ? { scale: 1.02 } : {}}
          whileTap={hintsUsed < maxHints ? { scale: 0.98 } : {}}
        >
          💡 提示 ({maxHints - hintsUsed})
        </motion.button>
        
        {/* 显示省名切换 */}
        <motion.button
          className={clsx(
            'w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
            {
              'bg-green-500 text-white': showProvinceNames,
              'bg-gray-200 text-gray-700 hover:bg-gray-300': !showProvinceNames
            }
          )}
          onClick={toggleProvinceNames}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          🏷️ {showProvinceNames ? '隐藏' : '显示'}省名
        </motion.button>
        
        {/* 显示简称拼音切换 */}
        <motion.button
          className={clsx(
            'w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
            {
              'bg-blue-500 text-white': showAbbreviations,
              'bg-gray-200 text-gray-700 hover:bg-gray-300': !showAbbreviations
            }
          )}
          onClick={toggleAbbreviations}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          🔤 {showAbbreviations ? '隐藏' : '显示'}简称
        </motion.button>
      </div>

      {/* 当前难度信息 */}
      <div className="bg-gray-50 rounded-lg p-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">难度:</span>
            <span className={clsx(
              'px-2 py-1 rounded-full text-xs font-medium',
              {
                'bg-green-100 text-green-800': getCurrentDifficultyInfo()?.color === 'green',
                'bg-yellow-100 text-yellow-800': getCurrentDifficultyInfo()?.color === 'yellow',
                'bg-red-100 text-red-800': getCurrentDifficultyInfo()?.color === 'red'
              }
            )}>
              {getCurrentDifficultyInfo()?.label}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">模式:</span>
            <span className="font-medium">
              {gameModes.find(m => m.value === mode)?.label}
            </span>
          </div>
        </div>
        
        {/* 下一个难度预告 */}
        {isGameCompleted && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex items-center justify-between">
                <span>下一个挑战:</span>
                <span className={clsx(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  {
                    'bg-green-100 text-green-800': getNextDifficultyInfo()?.color === 'green',
                    'bg-yellow-100 text-yellow-800': getNextDifficultyInfo()?.color === 'yellow',
                    'bg-red-100 text-red-800': getNextDifficultyInfo()?.color === 'red'
                  }
                )}>
                  {getNextDifficultyInfo()?.label}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 游戏完成提示 */}
      {isGameCompleted && (
        <motion.div
          className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-lg">🎉</span>
              <div className="flex-1">
                <div className="font-medium text-green-800 text-sm">
                  {getCurrentDifficultyInfo()?.label}难度完成！
                </div>
                <div className="text-xs text-green-600">
                  得分: {score} | 完成度: {getCompletionRate().toFixed(0)}%
                </div>
              </div>
            </div>
            
            <div className="text-xs text-gray-600 leading-tight">
              💡 点击地图上的按钮升级到{getNextDifficultyInfo()?.label}难度，或返回首页重新开始。
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
